import {Injectable} from "@angular/core";
import {
    BehaviorSubject,
    delay,
    filter,
    from,
    map,
    merge,
    Observable,
    startWith, Subscription,
    switchMap,
    take,
    tap,
    throwError
} from "rxjs";
import {IMqttMessage, MqttService} from "ngx-mqtt";
import {DeviceShadow} from "../models/shadow/device-shadow";
import {OutputActionRequest} from "../../units/models/output-action-request";
import {NetworkService} from "./network.service";
import {AuthenticationManager} from "./authentication-manager";
import {SignalingTransport} from "./signaling-transport";
import {WebRtcSignaling} from "../models/webrtc/web-rtc-signaling";
import {CallSessionUpdate} from "../models/webrtc/call/v2/call-session-update";
import {CallControlKind, CallControlMessage} from "../models/webrtc/call/v2/call-control-message";
import {CallUpdate} from "../models/webrtc/call/v2/call-update";
import {SignalingTransportV2} from "./signaling-transport-v2";
import {CallEntity, CallEntityKind} from "../models/webrtc/call/v2/call-entity";

export interface BridgeData {
    offerer: CallEntity;
    offererPeerId?: string;
    answerer: CallEntity;
    answererPeerId?: string;
}
import {CallSignalingMessage} from "../models/webrtc/call/v2/call-signaling-message";
import {SessionInfo} from "../models/webrtc/call/v1/session-info";
import {Call} from "../models/webrtc/call/call";
import {CallSessionControlKind, CallSessionControlMessage} from "../models/webrtc/call/v2/call-session-control-message";
import { environment } from "../../../environments/environment";

@Injectable()
export class IotService implements SignalingTransport, SignalingTransportV2 {

    private readonly _publicKey = environment.customAuthorizerPublicKey.trim();

    private readonly _iotEndpoint: string = "a128sd9dlbv9tq-ats.iot.eu-central-1.amazonaws.com";
    private readonly _customAuthorizerName: string = environment.customAuthorizer;

    private readonly sessionClientId: string = "infinity-web-" + (crypto.randomUUID?.() ?? Array.from(crypto.getRandomValues(new Uint8Array(16)), b => b.toString(16).padStart(2, '0')).join(''));

    private _connected: boolean = false;
    private _connecting: boolean = false;
    private _connected$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    private activeTopics = new Set<string>();

    constructor(private _mqtt: MqttService, private _auth: AuthenticationManager, network: NetworkService) {
        this.observeMqttConnectionState();
        merge(
            network.getNetworkStatus().pipe(filter(online => !online)),
            this._auth.userLoggedInUpdates().pipe(filter(loggedIn => !loggedIn)),
        ).subscribe(() => {
            // Delay disconnect to allow components to teardown their MQTT subscriptions
            // first via ngOnDestroy. Without this, the client enters "disconnecting" state
            // before subscriptions are cleaned up, causing "client disconnecting" errors.
            setTimeout(() => {
                try {
                    this._mqtt.disconnect();
                } catch (e) {
                    // Ignore disconnect errors (client may already be disconnected)
                }
            }, 0);
        });
    }

    public getSessionClientId(): string {
        return this.sessionClientId;
    }

    getDeviceShadow(thingName: string): Observable<DeviceShadow | null> | null {
        if (thingName?.length === 0) {
            return null;
        }

        if (!this._connected) {
            this.connect();
        }

        const prefix = `$aws/things/${thingName}/shadow`;

        return this._connected$.pipe(
            filter(connected => connected),
            take(1),
            switchMap(() => {
                const updates$ = this._mqtt.observe(`${prefix}/update/documents`).pipe(
                    map(value => {
                        const decoded = this.decode(value);
                        const status = JSON.parse(decoded)?.current?.state?.reported?.status;
                        return status as DeviceShadow;
                    })
                );

                const accepted$ = this._mqtt.observe(`${prefix}/get/accepted`).pipe(
                    take(1),
                    map(value => {
                        const decoded = this.decode(value);
                        const status = JSON.parse(decoded)?.state?.reported?.status;
                        return status as DeviceShadow;
                    })
                );

                return merge(accepted$, updates$).pipe(
                    startWith(null),
                    delay(200),
                    tap(value => {
                        if (value === null) {
                            this._mqtt.publish(`${prefix}/get`, '{}').subscribe();
                        }
                    }),
                    filter(v => v !== null)
                );
            })
        );
    }

    publishOutputAction(deviceId: string, entityId: string, installationId: string, address: string) {
        const topic = `things/${deviceId}/${installationId}/${entityId}/${this.sessionClientId}/action`;
        const message = {
            output: {
                address,
                action: "toggle"
            }
        } as OutputActionRequest;
        this._mqtt.publish(topic, JSON.stringify(message)).subscribe();
    }

    //#region V2 Call

    public publishOnSessionControl(call: Call, sessionId: string, kind: CallSessionControlKind, bridgeData?: BridgeData): void {
        if (!call) return;

        const topic = call.entityKind == "unit"
            ? `${environment.mqttTopicPrefix}/units/${call.entityId}/${this.sessionClientId}/session/control`
            : `${environment.mqttTopicPrefix}/pbxs/${call.entityId}/${this.sessionClientId}/session/control`

        const message: CallSessionControlMessage = {
            id: sessionId,
            call_id: call.callId,
            installation_id: call.installationId,
            kind: kind,
            ...(bridgeData && {
                offerer: bridgeData.offerer,
                offerer_peer_id: bridgeData.offererPeerId,
                answerer: bridgeData.answerer,
                answerer_peer_id: bridgeData.answererPeerId
            })
        };

        if (!this._connected) {
            this.connect();
        }

        this._connected$.pipe(
            filter(connected => connected),
            take(1),
            switchMap(() => {
                return this._mqtt.publish(topic, JSON.stringify(message));
            })
        ).subscribe();
    }

    public subscribeToSessionUpdate(entityId: string, entityKind: CallEntityKind): Observable<CallSessionUpdate> {
        let topic: string;
        switch (entityKind) {
            case "unit":
                topic = `${environment.mqttTopicPrefix}/units/${entityId}/${this.sessionClientId}/session/update`;
                break;
            case "pbx":
                topic = `${environment.mqttTopicPrefix}/pbxs/${entityId}/${this.sessionClientId}/session/update`;
                break;
        }

        if (!this._connected) {
            this.connect();
        }

        return this._connected$.pipe(
            filter(connected => connected),
            take(1),
            switchMap(() => {
                return this.observeMessages<CallSessionUpdate>(topic!);
            })
        );
    }

    public subscribeToCallUpdate(entityId: string, entityKind: CallEntityKind): Observable<CallUpdate> {
        let topic: string;

        switch (entityKind) {
            case CallEntityKind.Unit:
                topic = `${environment.mqttTopicPrefix}/units/${entityId}/${this.sessionClientId}/call/update`;
                break;
            case CallEntityKind.PBX:
                topic = `${environment.mqttTopicPrefix}/pbxs/${entityId}/${this.sessionClientId}/call/update`;
                break;
        }

        if (!this._connected) {
            this.connect();
        }

        return this._connected$.pipe(
            filter(connected => connected),
            take(1),
            switchMap(() => {
                return this.observeMessages<CallUpdate>(topic!)
            })
        );
    }

    publishCreateRequestOnCallControl$(
        callId: string,
        entityId: string,
        entityKind: CallEntityKind,
        remoteEntityId: string,
        remoteEntityKind: CallEntityKind,
        installationId: string
    ): Observable<void> {
        const topic = this.getCallControlTopic(entityId, entityKind);
        return this.publishCallControlAction$(callId, topic, CallControlKind.Start, remoteEntityId, remoteEntityKind, installationId);
    }

    publishForwardRequestOnCallControl$(
        callId: string,
        entityId: string,
        entityKind: CallEntityKind,
        remoteEntityId: string,
        remoteEntityKind: CallEntityKind,
        installationId: string
    ): Observable<void> {
        if (entityKind !== CallEntityKind.PBX) {
            return throwError(() => new Error("Forwarding is only supported for PBX entities."));
        }
        
        const topic: string = this.getCallControlTopic(entityId, entityKind);
        return this.publishCallControlAction$(callId, topic, CallControlKind.Forward, remoteEntityId, remoteEntityKind, installationId);
    }

    publishCancelRequestOnCallControl$(
        callId: string,
        entityId: string,
        entityKind: CallEntityKind,
        remoteEntityId: string,
        remoteEntityKind: CallEntityKind,
        installationId: string
    ): Observable<void> {
        const topic = this.getCallControlTopic(entityId, entityKind);
        return this.publishCallControlAction$(callId, topic, CallControlKind.Cancel, remoteEntityId, remoteEntityKind, installationId);
    }

    publishTimeoutRequestOnCallControl$(
        callId: string,
        entityId: string,
        entityKind: CallEntityKind,
        remoteEntityId: string,
        remoteEntityKind: CallEntityKind,
        installationId: string
    ): Observable<void> {
        const topic = this.getCallControlTopic(entityId, entityKind);
        return this.publishCallControlAction$(callId, topic, CallControlKind.Timeout, remoteEntityId, remoteEntityKind, installationId);
    }

    publishCloseRequestOnCallControl$(
        callId: string,
        entityId: string,
        entityKind: CallEntityKind,
        remoteEntityId: string,
        remoteEntityKind: CallEntityKind,
        installationId: string
    ): Observable<void> {
        const topic = this.getCallControlTopic(entityId, entityKind);
        return this.publishCallControlAction$(callId, topic, CallControlKind.Close, remoteEntityId, remoteEntityKind, installationId);
    }

    publishCallSignalingMessage(
        installationId: string,
        entityId: string,
        entityKind: CallEntityKind,
        message: CallSignalingMessage,
        remotePeerId?: string
    ): void {
        let topic: string;
        switch (entityKind) {
            case CallEntityKind.Doorstation:
            case CallEntityKind.Intercom:
                topic = `${environment.mqttTopicPrefix}/installations/${installationId}/${entityId}/session/message`;
                break;

            case CallEntityKind.Unit:
            case CallEntityKind.PBX:
                topic = `${environment.mqttTopicPrefix}/installations/${installationId}/${entityId}/${remotePeerId}/session/message`;
                break;
        }

        this._mqtt.publish(topic, JSON.stringify(message)).subscribe();
    }

    subscribeToSessionMessage(installationId: string, entityId: string): Observable<CallSignalingMessage> {
        const topic = `${environment.mqttTopicPrefix}/installations/${installationId}/${entityId}/${this.sessionClientId}/session/message`;

        return this._connected$.pipe(
            filter(connected => connected),
            take(1),
            switchMap(() => {
                return this.observeMessages<CallSignalingMessage>(topic);
            })
        );
    }

    //#endregion V2 Call

    //#region V1 Call

    subscribeToSignalingMessage(
        deviceId: string,
        installationId: string,
        unitId: string,
        clientId: string
    ): Observable<WebRtcSignaling> {
        if (!deviceId || !installationId || !unitId || !clientId) {
            return throwError(() => new Error("Invalid parameters to subscribe to signaling messages."));
        }

        const topic = `things/${deviceId}/${installationId}/${unitId}/${clientId}/webrtc/response`;

        if (!this._connected) {
            this.connect();
        }

        return this._connected$.pipe(
            filter(connected => connected),
            take(1),
            switchMap(() => {
                return this._mqtt.observe(topic).pipe(
                    map(value => JSON.parse(this.decode(value)) as WebRtcSignaling)
                )
            })
        );
    }

    sendSignalingMessage(
        deviceId: string,
        installationId: string,
        unitId: string,
        clientId: string,
        message: WebRtcSignaling
    ): void {
        if (!deviceId || !installationId || !unitId || !clientId) {
            throw (new Error("Invalid parameters to send signaling message."));
        }
        const topic = `things/${deviceId}/${installationId}/${unitId}/${clientId}/webrtc/request`;
        this._mqtt.publish(topic, JSON.stringify(message)).subscribe();
    }

    subscribeToJoinStatus(deviceId: string, installationId: string, entityId: string): Observable<SessionInfo> {
        if (!deviceId || !installationId || !entityId) {
            return throwError(() => new Error("Invalid parameters to subscribe to join status."));
        }

        if (!this._connected) {
            this.connect();
        }

        return this._connected$.pipe(
            filter(connected => connected),
            take(1),
            switchMap(() => {
                return this.observeMessages<SessionInfo>(
                    `things/${deviceId}/${installationId}/${entityId}/${this.sessionClientId}/join/status`
                );
            })
        );
    }

    publishViewOnJoin(deviceId: string, installationId: string, entityId: string) {
        if (!deviceId || !installationId || !entityId) {
            throw (new Error("Invalid parameters to publish on join."));
        }

        if (!this._connected) {
            this.connect();
        }

        this._connected$.pipe(
            filter(connected => connected),
            take(1),
            switchMap(() => {
                return this._mqtt.publish(`things/${deviceId}/${installationId}/${entityId}/${this.sessionClientId}/join`,
                    '{"action": 0, "clientKind": 1}')
            })
        ).subscribe();
    }

    publishTalkRequestOnJoin(deviceId: string, installationId: string, entityId: string, clientId: string) {
        if (!deviceId || !installationId || !entityId || !clientId) {
            throw (new Error("Invalid parameters to publish on join."));
        }

        if (!this._connected) {
            this.connect();
        }

        const deviceJoinTopic = `things/${deviceId}/${installationId}/${entityId}/${this.sessionClientId}/join`;

        this._connected$.pipe(
            filter(connected => connected),
            take(1),
            switchMap(() => {
                return this._mqtt.publish(deviceJoinTopic, `{"action": 1, "clientId": "${clientId}"}`)
            })
        ).subscribe();
    }

    //#endregion V1 Call

    //#region Internal MQTT utilities

    private observeMessages<T>(topic: string): Observable<T> {
        return new Observable<T>(observer => {

            const alreadySubscribed = this.activeTopics.has(topic);

            if (!alreadySubscribed) {
                this.activeTopics.add(topic);
            }

            let subAcked = alreadySubscribed;

            const msgSub = this._mqtt.observe(topic).subscribe({
                next: msg => {
                    try {
                        observer.next(JSON.parse(this.decode(msg)) as T);
                    } catch (e) {
                        observer.error(e);
                    }
                },
                error: err => observer.error(err)
            });

            let subackSub: Subscription | undefined;
            let timeout: any;

            if (!alreadySubscribed) {
                subackSub = this._mqtt.onSuback.pipe(
                    filter(evt => evt.filter === topic),
                    take(1)
                ).subscribe(evt => {
                    subAcked = true;

                    if (!evt.granted) {
                        observer.error(
                            new Error(`MQTT subscribe rejected for topic: ${topic}`)
                        );
                    }
                });

                timeout = setTimeout(() => {
                    if (!subAcked) {
                        observer.error(
                            new Error(`MQTT subscribe timeout for topic: ${topic}`)
                        );
                    }
                }, 5000);
            }

            return () => {
                if (timeout) {
                    clearTimeout(timeout);
                }
                msgSub.unsubscribe();
                subackSub?.unsubscribe();
            };
        });
    }

    private decode = (msg: IMqttMessage): string => new TextDecoder().decode(msg.payload);

    private connect(): void {
        if (this._connected || this._connecting) {
            return;
        }

        this._connecting = true;

        this._auth.getAccessToken().pipe(
            switchMap(accessToken => {
                if (accessToken === null)
                    throw (new Error("Access token is null, cannot connect to IoT service."));
                return from(this.getIoTUrl(accessToken));
            })
        ).subscribe(url => this._mqtt.connect({url: url}));
    }

    private observeMqttConnectionState(): void {
        this._mqtt.onConnect.subscribe(() => {
            this._connected = true;
            this._connected$.next(true);
            this._connecting = false;
        });
        this._mqtt.onError.subscribe(() => {
            this._connected = false;
            this._connected$.next(false);
            this._connecting = false;
        });
        this._mqtt.onEnd.subscribe(() => {
            this._connected = false;
            this._connected$.next(false);
            this._connecting = false;
        });
        this._mqtt.onClose.subscribe(() => {
            this._connected = false;
            this._connected$.next(false);
            this._connecting = false;
        });
    }

    private async getIoTUrl(token: string): Promise<string> {
        const signature = await this.signTokenWithRSA(this._publicKey, token);
        return `wss://${this._iotEndpoint}/mqtt?` +
            `x-amz-customauthorizer-name=${encodeURIComponent(this._customAuthorizerName)}` +
            `&Token=${encodeURIComponent(token)}` +
            `&x-amz-customauthorizer-signature=${encodeURIComponent(signature)}`;
    }

    private async signTokenWithRSA(privateKeyPem: string, token: string): Promise<string> {
        const keyData = this.convertPemToArrayBuffer(privateKeyPem);
        const privateKey = await crypto.subtle.importKey(
            "pkcs8",
            keyData,
            {
                name: "RSASSA-PKCS1-v1_5",
                hash: "SHA-256"
            },
            false,
            ["sign"]
        );
        const signature = await crypto.subtle.sign(
            "RSASSA-PKCS1-v1_5",
            privateKey,
            new TextEncoder().encode(token)
        );
        return btoa(String.fromCharCode(...new Uint8Array(signature)));
    }

    private convertPemToArrayBuffer(pem: string): ArrayBuffer {
        const b64 = pem
            .replace(/-----BEGIN PRIVATE KEY-----/, '')
            .replace(/-----END PRIVATE KEY-----/, '')
            .replace(/\s/g, '');
        const binary = atob(b64);
        const buffer = new ArrayBuffer(binary.length);
        const view = new Uint8Array(buffer);
        for (let i = 0; i < binary.length; i++) {
            view[i] = binary.charCodeAt(i);
        }
        return buffer;
    }

    private publishCallControlAction$(
        callId: string,
        topic: string,
        action: CallControlKind,
        remoteEntityId: string,
        remoteEntityKind: CallEntityKind,
        installationId: string
    ): Observable<void> {
        const createCallRequest: CallControlMessage = {
            id: callId,
            installation_id: installationId,
            callee: {
                id: remoteEntityId,
                kind: remoteEntityKind
            },
            kind: action
        };

        const payload = JSON.stringify(createCallRequest);

        if (!this._connected) {
            this.connect();
        }

        return this._connected$.pipe(
            filter(connected => connected),
            take(1),
            switchMap(() => {
                return this._mqtt.publish(topic, payload);
            })
        );
    }

    private getCallControlTopic(entityId: string, entityKind: CallEntityKind): string {
        if (entityKind != CallEntityKind.Unit && entityKind != CallEntityKind.PBX) {
            throwError(() => new Error("CallControlMessage can only be sent from Unit or PBX entities."));
        }

        return `${environment.mqttTopicPrefix}/${entityKind.toString()}s/${entityId}/${this.sessionClientId}/call/control`;
    }

    //#endregion Internal MQTT utilities
}
